// frontend/src/components/CodeAdvancedFields.jsx
import React from "react";
import PropTypes from "prop-types";
import { LABEL_CLS, INPUT_CLS } from "./QrPayloadFields";

/** Flatten a folder tree into indented <option> rows. */
function flattenFolders(nodes, depth = 0) {
  const out = [];
  for (const n of nodes) {
    out.push({ id: n.id, label: `${"— ".repeat(depth)}${n.name}` });
    if (n.children && n.children.length) {
      out.push(...flattenFolders(n.children, depth + 1));
    }
  }
  return out;
}

/**
 * Whole days from now until a `datetime-local` value, or null if unset/past.
 * @param {string} localValue
 * @returns {number|null}
 */
function daysUntil(localValue) {
  if (!localValue) return null;
  const ms = new Date(localValue).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.round(ms / 86_400_000);
}

/**
 * CodeAdvancedFields — shared folder / tags / expiry / scheduling / platform
 * controls used by the create and edit modals (P14-G8).
 *
 * Fully controlled: the parent owns a flat `value` object and merges the
 * partials emitted by `onChange`. The parent is responsible for turning the
 * `datetime-local` strings into ISO timestamps when it builds the request.
 *
 * value shape:
 *   { folderId, tagIds, expiresAt, activeFrom, platformEnabled,
 *     iosUrl, androidUrl, defaultUrl }
 */
function CodeAdvancedFields({ folders, tags, value, onChange }) {
  const folderOptions = flattenFolders(folders);
  const expiryDays = daysUntil(value.expiresAt);

  const toggleTag = (tagId) => {
    const next = value.tagIds.includes(tagId)
      ? value.tagIds.filter((t) => t !== tagId)
      : [...value.tagIds, tagId];
    onChange({ tagIds: next });
  };

  return (
    <div className="space-y-4 border-t border-gray-100 pt-4">
      {/* Folder */}
      <div>
        <label className={LABEL_CLS} htmlFor="adv-folder">
          Folder <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <select
          id="adv-folder"
          className={INPUT_CLS}
          value={value.folderId || ""}
          onChange={(e) => onChange({ folderId: e.target.value })}
        >
          <option value="">No folder (root)</option>
          {folderOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div>
          <span className={LABEL_CLS}>Tags</span>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => {
              const active = value.tagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleTag(tag.id)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? "text-white border-transparent"
                      : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50"
                  }`}
                  style={active ? { backgroundColor: tag.color_hex } : undefined}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: active ? "#fff" : tag.color_hex,
                    }}
                  />
                  {tag.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Scheduling: active from + expires at */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={LABEL_CLS} htmlFor="adv-active-from">
            Active from{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="adv-active-from"
            type="datetime-local"
            className={INPUT_CLS}
            value={value.activeFrom || ""}
            onChange={(e) => onChange({ activeFrom: e.target.value })}
          />
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor="adv-expires-at">
            Expires at{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="adv-expires-at"
            type="datetime-local"
            className={INPUT_CLS}
            value={value.expiresAt || ""}
            onChange={(e) => onChange({ expiresAt: e.target.value })}
          />
          {expiryDays !== null && (
            <p
              className={`mt-1 text-xs ${
                expiryDays < 0 ? "text-red-500" : "text-gray-400"
              }`}
            >
              {expiryDays < 0
                ? "This date is in the past."
                : expiryDays === 0
                  ? "Expires today."
                  : `Expires in ${expiryDays} day${expiryDays === 1 ? "" : "s"}.`}
            </p>
          )}
        </div>
      </div>

      {/* Platform redirect */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">
              Platform redirect
            </p>
            <p className="text-xs text-gray-400">
              Send iOS / Android scanners to different URLs
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={value.platformEnabled}
              onChange={(e) => onChange({ platformEnabled: e.target.checked })}
            />
            <div
              className="w-10 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600
              after:content-[''] after:absolute after:top-[2px] after:left-[2px]
              after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all
              peer-checked:after:translate-x-4"
            />
          </label>
        </div>
        {value.platformEnabled && (
          <div className="mt-3 space-y-2">
            <input
              type="url"
              aria-label="iOS URL"
              placeholder="iOS URL (e.g. App Store link)"
              className={INPUT_CLS}
              value={value.iosUrl || ""}
              onChange={(e) => onChange({ iosUrl: e.target.value })}
            />
            <input
              type="url"
              aria-label="Android URL"
              placeholder="Android URL (e.g. Play Store link)"
              className={INPUT_CLS}
              value={value.androidUrl || ""}
              onChange={(e) => onChange({ androidUrl: e.target.value })}
            />
            <input
              type="url"
              aria-label="Default URL"
              placeholder="Default URL (everyone else)"
              className={INPUT_CLS}
              value={value.defaultUrl || ""}
              onChange={(e) => onChange({ defaultUrl: e.target.value })}
            />
          </div>
        )}
      </div>
    </div>
  );
}

CodeAdvancedFields.propTypes = {
  folders: PropTypes.array,
  tags: PropTypes.array,
  value: PropTypes.shape({
    folderId: PropTypes.string,
    tagIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    expiresAt: PropTypes.string,
    activeFrom: PropTypes.string,
    platformEnabled: PropTypes.bool,
    iosUrl: PropTypes.string,
    androidUrl: PropTypes.string,
    defaultUrl: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
CodeAdvancedFields.defaultProps = { folders: [], tags: [] };

/**
 * Build the platform_destinations object from the advanced-fields value, or
 * null when the toggle is off or no URL was entered.
 */
export function buildPlatformDestinations(v) {
  if (!v.platformEnabled) return null;
  const dests = {};
  if (v.iosUrl) dests.ios = v.iosUrl.trim();
  if (v.androidUrl) dests.android = v.androidUrl.trim();
  if (v.defaultUrl) dests.default = v.defaultUrl.trim();
  return Object.keys(dests).length ? dests : null;
}

/**
 * Convert a `datetime-local` string to an ISO-8601 timestamp, or null.
 * @param {string} localValue
 * @returns {string|null}
 */
export function localToIso(localValue) {
  if (!localValue) return null;
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Convert an ISO timestamp to a `datetime-local`-compatible string in the
 * browser's local zone, or "" when unset.
 * @param {string|null} iso
 * @returns {string}
 */
export function isoToLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Shift by the local offset so toISOString's slice reflects local wall time.
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export default CodeAdvancedFields;
